import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { formatDubai } from '../../../utils/dubaiTime';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faPlus, faShieldHalved } from '@fortawesome/free-solid-svg-icons';
import { SchemeBadge, cardLast4, cardLabel, cardBrandLabel } from './savedCardUtils';
import { CARD_CONSENT_TEXT } from './cardConsent';
import {
    AUTO_CHARGE_UI_ENABLED,
    AUTO_PAY_TITLE,
    AUTO_PAY_BLURB,
    AUTO_PAY_OFF_GUARANTEE,
    AUTO_PAY_ON_SUMMARY,
    readAutoPayEnabled,
    saveAutoPayEnabled,
} from './autoPay';
import useBfcacheReset from '../useBfcacheReset';
import HostedCardPanel from '../payments/HostedCardPanel';
import { paymentErrorMessage } from '../paymentErrors';
import './savedCards.css';

// Account-page "Payment methods" section (rendered inside ClientProfile). Lists
// the client's saved cards (scheme, •••• last4, expiry, added date), lets them
// remove one (confirm dialog), and add one via POST /api/cards/setup — a
// temporary 1 AED verification HOLD on the hosted paypage that stores the
// token. The hold is released (voided) automatically: nothing is captured and
// NO wallet row is created (owner 2026-07-18 — the old rail's 1 AED wallet
// top-up is gone). Adding requires the explicit consent checkbox with the full
// CARD_CONSENT_TEXT (PLAN_payments.md — saved-card transparency).
//
// It is ALSO the one place the client signs up for (or out of) automatic
// payments — see autoPay.js for why that is a separate decision from saving a
// card. Nothing on this page may describe saving a card as consenting to be
// charged automatically.
export default function PaymentMethodsSection() {
    const [cards, setCards] = useState([]);
    const [autoDeps, setAutoDeps] = useState(0); // bookings depending on a saved card to auto-charge
    const [loading, setLoading] = useState(true);
    const [available, setAvailable] = useState(true); // gateway not disabled
    const [mitEnabled, setMitEnabled] = useState(false);

    // The auto-payment sign-up. Starts OFF and only ever turns on from an
    // explicit `auto_pay_enabled: true` — never inferred from having a card.
    const [autoPay, setAutoPay] = useState(false);
    const [autoPaySaving, setAutoPaySaving] = useState(false);

    const [adding, setAdding] = useState(false);
    const [consent, setConsent] = useState(false);
    // EMBEDDED CARD RAIL (2026-08-16). The card is typed here, on the account
    // page, and the verification AUTH runs against it — no hop out to the
    // paypage and back through the return page just to store a token. 'off'
    // means the SDK could not mount; the redirect underneath is untouched.
    const [hostedRail, setHostedRail] = useState('probe');
    const [enteringCard, setEnteringCard] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // P3 — the FIRST card's form carries the auto-payment sign-up (owner). Separate state from `consent` on purpose: they are two different
    // agreements and only one of them is required to save a card. Defaults
    // false and is never pre-ticked — the gate stays a gate.
    const [signUpAutoPay, setSignUpAutoPay] = useState(false);

    const [removeTarget, setRemoveTarget] = useState(null);

    const [removing, setRemoving] = useState(false);

    const fetchCards = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axios.get('/api/cards');
            setCards(Array.isArray(res.data?.cards) ? res.data.cards : []);
            setAutoDeps(Number(res.data?.auto_charge_dependents) || 0);
            setAutoPay(readAutoPayEnabled(res.data));
        } catch (err) {
            // Cards may not be deployed yet — the account page must not break.
            if (err.response?.status !== 404) console.error('Error loading saved cards:', err);
            setCards([]);
            setAutoDeps(0);
            setAutoPay(false); // fail closed: unknown state is never "signed up"
        } finally {
            setLoading(false);
        }
    }, []);

    // ── Optional client-chosen card name (2026-08-13) ─────────────────
    // Edited INLINE in the row — no browser prompt. Cosmetic only: PATCH
    // /api/cards/:id never touches the token, and an empty value clears back
    // to scheme + last four.
    const [editingId, setEditingId] = useState(null);
    const [draftName, setDraftName] = useState('');
    const [savingName, setSavingName] = useState(false);

    const startRename = (card) => {
        setEditingId(card.id);
        setDraftName(String(card.nickname || '').trim());
    };
    const cancelRename = () => {
        setEditingId(null);
        setDraftName('');
    };
    const saveRename = async (card) => {
        if (savingName) return;
        setSavingName(true);
        try {
            await axios.patch(`/api/cards/${card.id}`, { nickname: draftName.trim() });
            await fetchCards();
            setEditingId(null);
            setDraftName('');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Could not save the card name.');
        } finally {
            setSavingName(false);
        }
    };

    useEffect(() => {
        fetchCards();
        // Gateway availability + MIT flag drive the copy (best effort).
        (async () => {
            try {
                const res = await axios.get('/api/payments/mode');
                setAvailable(res.data?.mode !== 'disabled');
                setMitEnabled(!!res.data?.mit_enabled);
            } catch {
                setAvailable(true);
            }
        })();
    }, [fetchCards]);

    // Restored from the paypage's Back button (bfcache) → clear the frozen
    // "Opening secure page…" state so the section is usable again.
    useBfcacheReset(() => setSubmitting(false));

    const startAdd = () => {
        setConsent(false);
        // Reflect the sign-up they already have rather than resetting it to
        // false — a client who is enrolled and adding a second card is not
        // being asked to enrol again (owner).
        setSignUpAutoPay(autoPay);
        setAdding(true);
    };

    // Flip the sign-up. Optimistic, but reverted on failure — the switch must
    // never show "on" for a state the server did not accept, and vice versa.
    const toggleAutoPay = async () => {
        if (autoPaySaving) return;
        const next = !autoPay;
        setAutoPaySaving(true);
        setAutoPay(next);
        try {
            const saved = await saveAutoPayEnabled(next);
            setAutoPay(saved);
            toast.success(saved
                ? 'Automatic payments are on.'
                : 'Automatic payments are off — nothing will be charged automatically.');
            await fetchCards();
        } catch (err) {
            console.error('Error updating auto-payment sign-up:', err);
            setAutoPay(!next);
            toast.error(paymentErrorMessage(err, 'Could not update your automatic payment setting.'));
        } finally {
            setAutoPaySaving(false);
        }
    };

    // The sign-up (when ticked) is recorded before either rail starts, for the
    // same reason it always was: a card saved without the enrolment the client
    // just asked for is the wrong outcome, and so is the reverse.
    const recordSignUpIfAsked = async () => {
        if (!(signUpAutoPay && !autoPay)) return true;
        try {
            const saved = await saveAutoPayEnabled(true);
            setAutoPay(saved);
            return true;
        } catch (err) {
            console.error('Error recording auto-payment sign-up:', err);
            toast.error(paymentErrorMessage(err, 'Could not switch on automatic payments. Your card was not saved — please try again.'));
            return false;
        }
    };

    // EMBEDDED: reveal the card fields on this page. Nothing reaches the
    // gateway until Pay is pressed, which is what startCardVerification does.
    const beginCardEntry = async () => {
        if (!consent || submitting) return;
        setSubmitting(true);
        const ok = await recordSignUpIfAsked();
        setSubmitting(false);
        if (ok) setEnteringCard(true);
    };

    // The panel hands us the SDK's session; the server completes the 1 AED
    // AUTH against it and returns the payment object for any 3DS challenge.
    const startCardVerification = async (sessionId) => {
        try {
            const res = await axios.post('/api/cards/setup', {
                save_card_consent: true,
                session_id: sessionId,
            });
            if (!res.data?.payment_response) {
                toast.error('Could not start card verification. Please try again.');
                return null;
            }
            return res.data;
        } catch (err) {
            console.error('Error starting card setup:', err);
            toast.error(paymentErrorMessage(err, 'Could not start card verification. Please try again.'));
            return null;
        }
    };

    // Verified: the token is stored and the hold is released server-side.
    const onCardVerified = async () => {
        setEnteringCard(false);
        setAdding(false);
        setConsent(false);
        setSignUpAutoPay(false);
        toast.success('Card saved. The 1 AED hold is released automatically.');
        await fetchCards();
    };

    const handleAddCard = async () => {
        if (!consent || submitting) return;
        setSubmitting(true);
        try {
            // The sign-up is recorded BEFORE we hand the client to the paypage,
            // because that navigation never comes back to this component. Order
            // matters the other way too: if the sign-up PUT fails we stop and
            // say so instead of silently saving a card without the enrolment
            // the client just asked for. Enrolling with no card yet is safe —
            // there is nothing to charge, and the block below says exactly that.
            if (signUpAutoPay && !autoPay) {
                try {
                    const saved = await saveAutoPayEnabled(true);
                    setAutoPay(saved);
                } catch (err) {
                    console.error('Error recording auto-payment sign-up:', err);
                    toast.error(paymentErrorMessage(err, 'Could not switch on automatic payments. Your card was not saved — please try again.'));
                    setSubmitting(false);
                    return;
                }
            }
            const res = await axios.post('/api/cards/setup', { save_card_consent: true });
            if (res.data?.payment_url) {
                window.location.assign(res.data.payment_url);
                return; // navigating away — keep submitting on
            }
            toast.error('Could not start card verification. Please try again.');
            setSubmitting(false);
        } catch (err) {
            console.error('Error starting card setup:', err);
            toast.error(paymentErrorMessage(err, 'Could not start card verification. Please try again.'));
            setSubmitting(false);
        }
    };

    const handleRemove = async () => {
        if (!removeTarget || removing) return;
        setRemoving(true);
        try {
            await axios.delete(`/api/cards/${removeTarget.id}`);
            toast.success('Card removed.');
            setRemoveTarget(null);
            await fetchCards();
        } catch (err) {
            console.error('Error removing card:', err);
            toast.error(paymentErrorMessage(err, 'Failed to remove the card.'));
        } finally {
            setRemoving(false);
        }
    };

    const hasCards = cards.length > 0;

    // P3 (noted) — the switch, and the "Not signed up…" reassurance that
    // comes with it, only make sense once there is a card. With nothing on
    // file, "your saved cards stay on file for faster checkout only" describes
    // cards the client does not have, and a switch for charging a card they
    // have not given us is noise. In that state the sign-up lives inside the
    // add-card form instead.
    //
    // The one exception is a client who IS enrolled but has no card (they
    // ticked the sign-up and abandoned the paypage): the block stays so they
    // can see it and switch it off. Hiding a live setting is never the answer.
    // Auto-payment sign-up is HIDDEN while AUTO_CHARGE_UI_ENABLED is off (owner
    // 2026-07-20: no unattended charging until MoTo/MIT is live). The card list,
    // add-card form and consent below all stay — saving a card is plain
    // faster-checkout storage and never depended on this block.
    const showAutoPayBlock = AUTO_CHARGE_UI_ENABLED && (hasCards || autoPay);

    return (
        <div className="cprof__card">
            <div className="cprof__cardTitle">Payment methods</div>
            {/* P2 — saving a card is ONLY about speed. The sentence that used
                to append ", and to auto-charge completed cleanings" is gone on
                purpose: automatic charging now belongs exclusively to the
                sign-up below, and no copy here may imply otherwise. */}
            <div className="hcard-pm-sub">
                Save a card to pay for bookings and top-ups faster. We never see or store your full card
                number — only a secure token from our payment provider.
                {/* The "…does not sign you up for automatic payments" clause is
                    itself an auto-payment reference, so it is dropped while the
                    feature is hidden (2026-07-20): there is nothing to sign
                    up for, so nothing to disclaim. */}
                {AUTO_CHARGE_UI_ENABLED && (
                    <>
                        {' '}
                        <strong>Saving a card does not sign you up for automatic payments</strong>
                        {showAutoPayBlock ? ' — that is the separate setting below.' : ' — that is a separate,'
                            + ' optional choice you can make when you add your card.'}
                    </>
                )}
            </div>

            {loading ? (
                <div className="hcard-empty">Loading your cards…</div>
            ) : cards.length === 0 ? (
                <div className="hcard-empty">
                    <FontAwesomeIcon icon={faCreditCard} style={{ marginRight: 8, opacity: 0.6 }} />
                    No saved cards yet.
                </div>
            ) : (
                <div className="hcard-list">
                    {cards.map((card) => (
                        <div className="hcard-row" key={card.id}>
                            <SchemeBadge scheme={card.scheme} />
                            <div className="hcard-row-main">
                                {/* Nickname first when the client gave one
                                    (2026-08-13) — "Personal Visa •••• 1111"
                                    beats a row of identical masked numbers. */}
                                {editingId === card.id ? (
                                    <div className="hcard-nameEdit">
                                        <input
                                            className="hcard-nameInput"
                                            type="text"
                                            value={draftName}
                                            maxLength={40}
                                            autoFocus
                                            placeholder={`e.g. Personal ${cardBrandLabel(card.scheme)}`}
                                            onChange={(e) => setDraftName(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') saveRename(card);
                                                if (e.key === 'Escape') cancelRename();
                                            }}
                                            disabled={savingName}
                                        />
                                        <button type="button" className="hcard-nameSave" onClick={() => saveRename(card)} disabled={savingName}>
                                            {savingName ? 'Saving…' : 'Save'}
                                        </button>
                                        <button type="button" className="hcard-nameCancel" onClick={cancelRename} disabled={savingName}>
                                            Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <div className="hcard-row-num">
                                        {String(card.nickname || '').trim()
                                            ? `${card.nickname.trim()} •••• ${cardLast4(card.masked_pan)}`
                                            : `•••• ${cardLast4(card.masked_pan)}`}
                                    </div>
                                )}
                                <div className="hcard-row-meta">
                                    {/* Card expiry is never shown to clients (2026-07-19). */}
                                    Saved card
                                    {/* Asia/Dubai render (item 8) */}
                                    {card.created_at
                                        ? ` · Added ${formatDubai(card.created_at, 'dd LLL yyyy')}`
                                        : ''}
                                </div>
                            </div>
                            {editingId !== card.id && (
                                <button
                                    type="button"
                                    className="hcard-rename"
                                    onClick={() => startRename(card)}
                                    disabled={removing}
                                >
                                    {String(card.nickname || '').trim() ? 'Rename' : 'Name it'}
                                </button>
                            )}
                            <button
                                type="button"
                                className="hcard-remove"
                                onClick={() => setRemoveTarget(card)}
                                disabled={removing}
                            >
                                Remove
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {!available && (
                <div className="hcard-empty" style={{ paddingTop: 10 }}>
                    Card payments are temporarily unavailable.
                </div>
            )}

            {available && !adding && (
                <button type="button" className="hcard-add-btn" onClick={startAdd}>
                    <FontAwesomeIcon icon={faPlus} /> Add card
                </button>
            )}

            {available && adding && (
                <div style={{ marginTop: 14 }}>
                    <div className="hcard-setup-note">
                        <FontAwesomeIcon icon={faShieldHalved} style={{ marginRight: 8 }} />
                        To save your card we place a <strong>temporary 1 AED hold</strong> to verify it.
                        The hold is released automatically — it is never charged. Your card is then
                        saved for future payments.
                    </div>
                    {/* P2 — this checkbox is a STORAGE disclosure, not a charge
                        mandate. CARD_CONSENT_TEXT (v3) no longer authorises
                        anything; it tells the client we never hold their card
                        details. Every word about being charged now lives with
                        the auto-payment sign-up, which is the only thing that
                        authorises it. See cardConsent.js for the backend
                        version bump this ships with. */}
                    <label className="hcard-consent">
                        <input
                            type="checkbox"
                            checked={consent}
                            onChange={(e) => setConsent(e.target.checked)}
                            disabled={submitting}
                        />
                        <span className="hcard-consent-text">
                            <strong className="hcard-consent-lead">
                                Your card details stay with our payment provider.
                            </strong>
                            <span className="hcard-consent-mandate">{CARD_CONSENT_TEXT}</span>
                            {/* P3 — this box GATES the primary button, so when it
                                is untouched the client needs to be told that,
                                not left to infer it from a disabled button. */}
                            {!consent && (
                                <span className="hcard-consent-required">
                                    Tick this box to continue.
                                </span>
                            )}
                        </span>
                    </label>

                    {/* P3 — FIRST CARD: the sign-up rides along with this form,
                        because a client with no card never reaches the switch
                        below (it is hidden until there is a card to charge).
                        Unticked by default, and ticking it is the whole consent
                        — same text as the switch, so the two can't drift.

                        WITH a card already on file this is deliberately absent:
                        adding a second card must not re-ask, and the switch
                        below already carries the setting. */}
                    {/* First-card auto-pay opt-in — hidden while the feature is
                        off (2026-07-20). Adding a card then only stores it. */}
                    {AUTO_CHARGE_UI_ENABLED && !hasCards && !autoPay && (
                        <label className="hcard-consent hcard-consent--autopay">
                            <input
                                type="checkbox"
                                checked={signUpAutoPay}
                                onChange={(e) => setSignUpAutoPay(e.target.checked)}
                                disabled={submitting}
                            />
                            <span className="hcard-consent-text">
                                <strong className="hcard-consent-lead">{AUTO_PAY_TITLE}</strong>
                                <span className="hcard-consent-mandate">{AUTO_PAY_BLURB}</span>
                                <span className="hcard-consent-qualifier">
                                    Optional — leave this unticked and nothing is ever charged without you
                                    starting the payment yourself. You can switch it on or off anytime here
                                    in Account.
                                </span>
                            </span>
                        </label>
                    )}

                    {AUTO_CHARGE_UI_ENABLED && hasCards && (
                        <div className="hcard-consent-qualifier">
                            This only adds a card. Your automatic payment setting is unchanged — it stays{' '}
                            <strong>{autoPay ? 'ON' : 'OFF'}</strong> and is switched below.
                        </div>
                    )}
                    {/* THE CARD IS TYPED HERE. The panel mounts the gateway's
                        own fields and runs any 3DS challenge in its overlay;
                        only when the SDK cannot mount does this fall back to
                        the paypage the button below used to open. */}
                    {enteringCard && hostedRail !== 'off' && (
                        <div className="hcard-embed">
                            <HostedCardPanel
                                payLabel="Save card (1 AED hold)"
                                amountAed={1}
                                /* No Digital Payments tab here: a wallet payment
                                   leaves us no token, so "add a card" through
                                   Apple Pay would save nothing. */
                                walletsEnabled={false}
                                startPayment={startCardVerification}
                                onUnavailable={() => { setHostedRail('off'); setEnteringCard(false); }}
                                onPaid={onCardVerified}
                                onFailed={() => { /* the panel states its own outcome and re-arms */ }}
                            />
                        </div>
                    )}

                    <div className="hcard-actions">
                        <button
                            type="button"
                            className="hcard-btn hcard-btn--ghost"
                            onClick={() => { setEnteringCard(false); setAdding(false); }}
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        {!(enteringCard && hostedRail !== 'off') && (
                            <button
                                type="button"
                                className="hcard-btn hcard-btn--primary"
                                onClick={hostedRail === 'off' ? handleAddCard : beginCardEntry}
                                disabled={!consent || submitting}
                                title={!consent ? 'Tick the box to authorise saving your card' : undefined}
                            >
                                {submitting
                                    ? (hostedRail === 'off' ? 'Opening secure page…' : 'Preparing…')
                                    : hostedRail === 'off' ? 'Continue to secure page' : 'Add card'}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* ── THE AUTO-PAYMENT SIGN-UP ──────────────────────────────────
                Self-contained, one switch, current state obvious at a glance.
                Off is stated as a guarantee, not as an absence.

                Gated on showAutoPayBlock (P3): with no card on file this whole
                block — including the "Not signed up…" reassurance — is not
                rendered, and the sign-up appears inside the add-card form
                instead. The switch is never the ONLY way to enrol, and it is
                never a second thing to fill in for someone who already has. */}
            {showAutoPayBlock && (
            <div className={`hcard-autopay ${autoPay ? 'is-on' : 'is-off'}`}>
                <div className="hcard-autopay-head">
                    <div className="hcard-autopay-copy">
                        <div className="hcard-autopay-title">{AUTO_PAY_TITLE}</div>
                        <div className="hcard-autopay-blurb">{AUTO_PAY_BLURB}</div>
                    </div>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={autoPay}
                        aria-label={AUTO_PAY_TITLE}
                        className={`hcard-switch ${autoPay ? 'is-on' : ''}`}
                        onClick={toggleAutoPay}
                        disabled={autoPaySaving || loading}
                    >
                        <span className="hcard-switch-knob" aria-hidden="true" />
                    </button>
                </div>

                <div className={`hcard-autopay-state ${autoPay ? 'is-on' : 'is-off'}`}>
                    <FontAwesomeIcon icon={faShieldHalved} aria-hidden="true" />
                    <span>
                        <strong>{autoPay ? 'Signed up' : 'Not signed up'}</strong>
                        {' — '}
                        {autoPay ? AUTO_PAY_ON_SUMMARY : AUTO_PAY_OFF_GUARANTEE}
                    </span>
                </div>

                {/* Enrolled but no card yet: the sign-up is real, it just has
                    nothing to charge. Say so rather than letting them believe
                    payments are already handled. */}
                {autoPay && !loading && !hasCards && (
                    <div className="hcard-autopay-note">
                        Add a card above to finish setting this up — until then we&apos;ll ask you to pay
                        each completed cleaning as usual.
                    </div>
                )}

                {/* Honest about the provider-side gate: the sign-up is stored,
                    but unattended charging only fires once MIT is live. */}
                {autoPay && !mitEnabled && (
                    <div className="hcard-autopay-note">
                        Automatic charging goes live once our payment provider enables it for your
                        account. Until then we&apos;ll ask you to pay each completed cleaning.
                    </div>
                )}

                {/* Turning off with bookings already queued to auto-charge is
                    the exact situation the client most needs explained. */}
                {!autoPay && autoDeps > 0 && (
                    <div className="hcard-autopay-note">
                        {autoDeps} existing booking{autoDeps === 1 ? '' : 's'} {autoDeps === 1 ? 'was' : 'were'} set
                        up to auto-charge. We&apos;ll ask you to pay {autoDeps === 1 ? 'it' : 'them'} instead.
                    </div>
                )}
            </div>
            )}

            {removeTarget && (() => {
                // Removing the LAST active card is blocked while auto-charge
                // bookings depend on it — the server enforces this too (409).
                const isLastCard = cards.length === 1;
                const blocked = isLastCard && autoDeps > 0;
                return (
                    <div
                        className="hcard-overlay"
                        onClick={(e) => { if (e.target === e.currentTarget && !removing) setRemoveTarget(null); }}
                    >
                        <div className="hcard-modal">
                            {blocked ? (
                                <>
                                    <h3 className="hcard-modal-title">Add another card first</h3>
                                    <p className="hcard-modal-text">
                                        You have <strong>{autoDeps} booking{autoDeps === 1 ? '' : 's'}</strong> set
                                        to auto-charge, and <strong>{cardLabel(removeTarget)}</strong> is your only
                                        saved card. Add another card first, or cancel those bookings — then you can
                                        remove this one.
                                    </p>
                                    <div className="hcard-modal-actions">
                                        <button
                                            type="button"
                                            className="hcard-btn hcard-btn--ghost"
                                            onClick={() => setRemoveTarget(null)}
                                        >
                                            Close
                                        </button>
                                        <button
                                            type="button"
                                            className="hcard-btn hcard-btn--primary"
                                            onClick={() => { setRemoveTarget(null); startAdd(); }}
                                        >
                                            Add another card
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <h3 className="hcard-modal-title">Remove card?</h3>
                                    <p className="hcard-modal-text">
                                        Remove <strong>{cardLabel(removeTarget)}</strong>?
                                        {cards.length > 1 && autoPay
                                            ? ' Your other saved card will be used for any auto-charge bookings.'
                                            : cards.length > 1
                                                ? ' Your other saved card stays on file.'
                                                : ' You can add a card again anytime.'}
                                    </p>
                                    <div className="hcard-modal-actions">
                                        <button
                                            type="button"
                                            className="hcard-btn hcard-btn--ghost"
                                            onClick={() => setRemoveTarget(null)}
                                            disabled={removing}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="hcard-btn hcard-btn--danger"
                                            onClick={handleRemove}
                                            disabled={removing}
                                        >
                                            {removing ? 'Removing…' : 'Remove card'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}
